import {
  Button,
  CrossLg,
  SettingsLg,
  H1,
  spacingHelper,
  ShareLg,
  BellLg,
  StarLg,
  paletteHelper,
  LockLg,
  SaveSearchLg,
} from '@construct-kit/core';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { MenuItem } from './MenuItem';

const fadeAnimation = keyframes`
  0% { opacity: 0; transform: translateY(-1rem); }
`;

const Wrapper = styled.div`
  position: fixed;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
  background-color: ${paletteHelper('backgroundPrimary')};
  text-align: left;
  animation-name: ${fadeAnimation};
  animation-duration: 0.3s;
  animation-timing-function: ease-out;

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    position: absolute;
    width: 17.5rem;
    height: auto;
    top: 100%;
    left: auto;
    right: 0;
    margin-top: ${spacingHelper('extraSmall')};
    background-color: transparent;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${spacingHelper('large medium medium')};

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    display: none;
  }
`;

const Menu = styled.nav`
  border-radius: ${({ theme }) => theme.border.radius.medium};
  border: solid 1px ${paletteHelper('backgroundSecondary')};
  margin: ${spacingHelper('0 medium medium')};
  background-color: ${paletteHelper('backgroundPrimary')};
  overflow: hidden;

  > li:not(:last-child) {
    border-bottom: solid 1px ${paletteHelper('backgroundSecondary')};
  }

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    margin: 0;
  }
`;

const List = styled.ul`
  > li:not(:last-child) {
    border-bottom: solid 1px ${paletteHelper('backgroundSecondary')};
  }
`;

const LogoutLg = styled(ShareLg)`
  transform: rotate(-90deg);
`;

type props = {
  setAccountMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  trackClickThrough: (element: string) => void;
};

const CLICK_THROUGH_ELEMENT_PREFIX = 'my-account';

export const AccountMenu: React.FC<props> = ({
  setAccountMenuOpen,
  trackClickThrough,
}) => {
  return (
    <Wrapper>
      <Header>
        <H1>Account</H1>
        <Button
          onClick={() => setAccountMenuOpen(false)}
          variant="naked"
          hideLabel
          compact
          icon={<CrossLg />}
        >
          Close
        </Button>
      </Header>
      <Menu role="menu" aria-label="Account menu">
        <List>
          <MenuItem
            href="/my-commercial/saved-properties"
            onClick={() =>
              trackClickThrough(
                `${CLICK_THROUGH_ELEMENT_PREFIX}:saved-properties`,
              )
            }
            icon={<StarLg />}
          >
            Saved properties
          </MenuItem>
          <MenuItem
            href="/my-commercial/saved-searches-and-email-alerts"
            onClick={() =>
              trackClickThrough(`${CLICK_THROUGH_ELEMENT_PREFIX}:search-alerts`)
            }
            icon={<SaveSearchLg />}
          >
            Saved searches & alerts
          </MenuItem>
          <MenuItem
            href="/my-commercial/account"
            onClick={() =>
              trackClickThrough(`${CLICK_THROUGH_ELEMENT_PREFIX}:settings`)
            }
            icon={<SettingsLg />}
          >
            Account settings
          </MenuItem>
          <MenuItem
            href="/my-commercial/notification-settings"
            onClick={() =>
              trackClickThrough(
                `${CLICK_THROUGH_ELEMENT_PREFIX}:notification-settings`,
              )
            }
            icon={<BellLg />}
          >
            Notification settings
          </MenuItem>
          <MenuItem
            href="/my-commercial/data-privacy-settings"
            onClick={() =>
              trackClickThrough(
                `${CLICK_THROUGH_ELEMENT_PREFIX}:data-privacy-settings`,
              )
            }
            icon={<LockLg />}
          >
            Data privacy settings
          </MenuItem>
          <MenuItem
            href="/sign-out"
            onClick={() =>
              trackClickThrough(`${CLICK_THROUGH_ELEMENT_PREFIX}:logout`)
            }
            icon={<LogoutLg />}
          >
            Sign out
          </MenuItem>
        </List>
      </Menu>
    </Wrapper>
  );
};
